import React, { useEffect, useState } from 'react';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { useAuth } from '@/contexts/AuthContext';
import {
  COD_FINANCE_ISSUE_ACTION_LABELS,
  COD_FINANCE_ISSUE_ACTIONS,
  COD_FINANCE_ISSUE_REASON_LABELS,
  COD_FINANCE_ISSUE_REASONS,
  type CodFinanceDepositBatch,
  type CodFinanceIssueAction,
  type CodFinanceIssueReason,
  type CodFinanceOrder,
  confirmCodFinance,
  flagCodFinanceIssue,
  matchCodDeposit,
} from '@/services/api/codOrders';
import { formatRupiah } from './CodBadges';

/**
 * Who the server will record as the actor, shown in every Leg 3 dialog footer.
 *
 * Not decoration. These three actions are the ones whose only control IS the
 * audit trail — and confirming is irreversible — so the name that will be
 * written belongs in view before the button is pressed, not discovered in the
 * timeline afterwards. The server takes the actor from the token regardless;
 * this is the operator's copy of the same fact.
 */
const ActorNote: React.FC = () => {
  const { user } = useAuth();
  const actor = user?.email || user?.displayName;
  if (!actor) return <span />;
  return (
    <div className="text-xs text-text-sub-600">
      Sebagai <b>{actor}</b>
    </div>
  );
};

/**
 * What a Leg 3 action will act on.
 *
 * ORDERS, not deposits. Matching and confirming started as whole-transfer
 * actions, which is the common case and still the default — but Finance also
 * has to settle part of a transfer: three of five orders appear on today's
 * statement and two do not. The deposit is still the unit that gets a bank
 * reference; it just stopped being the only selectable thing.
 *
 * `batches` rides along because the wording needs it — whose transfer, which
 * day, which reference — and `whole` is what lets the dialogs tell "this
 * transfer" from "part of it" without re-deriving it from two arrays.
 */
export type CodFinanceSelection = {
  /** The orders the action will move, in the order the operator sees them. */
  orders: CodFinanceOrder[];
  /** Every deposit those orders came from. */
  batches: CodFinanceDepositBatch[];
  /** True when `orders` is every order of every batch listed — no cherry-pick. */
  whole: boolean;
};

/**
 * Match a deposit to its bank statement line.
 *
 * The reference is REQUIRED and deliberately unvalidated beyond that: banks
 * print references differently and Finance types what they see on the statement,
 * so a format check would reject valid input the first time a new bank appears.
 * The server applies the same rule — present, trimmed, capped.
 *
 * The batch total is shown, not a per-order figure, because the total is what
 * appears on the statement. It is the full transfer even when a filter is on:
 * matching against a filtered subtotal would have Finance chasing a shortfall
 * that does not exist.
 */
export const MatchDepositModal: React.FC<{
  selection: CodFinanceSelection | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ selection, onClose, onDone }) => {
  const [bankRef, setBankRef] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const batches = selection?.batches ?? [];
  const single = batches.length === 1 ? batches[0] : null;

  useEffect(() => {
    // Pre-fill when re-matching within ONE deposit: a typo in a reference
    // should be a two-character fix, not a retype. Across several it stays
    // blank — pre-filling from whichever batch happened to be first would put
    // one transfer's reference onto the others without anyone choosing that.
    setBankRef(single?.financeBankRef ?? '');
    setNote('');
  }, [single]);

  if (!selection || selection.orders.length === 0) return null;

  // Flagged orders are skipped server-side rather than refused, so they are
  // excluded here too and the count below says what will actually move.
  const matchable = selection.orders.filter((o) => !o.financeIssueReason);
  const flagged = selection.orders.length - matchable.length;
  // The TRANSFER total when whole deposits were picked — that is the figure on
  // the statement, and matching against anything else would have Finance
  // chasing a shortfall that does not exist. But a hand-picked subset has no
  // statement figure of its own, so there the honest number is what was
  // actually selected.
  const total = selection.whole
    ? batches.reduce((sum, b) => sum + b.totalAmount, 0)
    : matchable.reduce((sum, o) => sum + o.depositedAmount, 0);
  const batchOrderCount = batches.reduce((sum, b) => sum + b.totalOrders, 0);
  const partial = !selection.whole;
  const clients = new Set(matchable.map((o) => o.clientName ?? '—'));

  const submit = async () => {
    if (!bankRef.trim()) {
      toast.error('Referensi mutasi bank wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const res = await matchCodDeposit({
        // One order or forty — the endpoint has always taken a list, so a
        // per-row match is the same call with one id in it.
        shipmentIds: matchable.map((o) => o.id),
        bankRef: bankRef.trim(),
        matchNote: note.trim() || undefined,
      });
      toast.success(
        `${res.affected.length} pesanan dicocokkan${
          res.skipped.length
            ? ` · ${res.skipped.length} dilewati (bermasalah)`
            : ''
        }`
      );
      onDone();
      onClose();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title="Cocokkan dengan mutasi bank"
      subtitle={
        single
          ? `${single.depositedBy} · ${single.depositedOn}`
          : `${batches.length} setoran · satu referensi mutasi`
      }
      footer={
        // Actor on the left, actions on the right — the prototype's layout, and
        // it earns the space: the reference typed here becomes the evidence in
        // the audit trail, so whose name goes on it belongs in view.
        <div className="flex items-center justify-between gap-3">
          <ActorNote />
          <div className="flex gap-2">
            <Button style="stroke" onClick={onClose}>
              Batal
            </Button>
            <Button
              onClick={submit}
              disabled={saving || matchable.length === 0}
            >
              {saving ? 'Menyimpan…' : `Tandai cocok (${matchable.length})`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-stroke-soft-200 p-3">
          <div className="text-xs text-text-sub-600">
            {partial
              ? 'Nilai pesanan terpilih'
              : single
                ? 'Nilai setoran'
                : `Nilai ${batches.length} setoran`}
          </div>
          <div className="text-title-h5">{formatRupiah(total)}</div>
          <div className="mt-1 text-xs text-text-sub-600">
            {/* On a partial selection the denominator is the point: it is the
                operator's own check that they picked what they meant to, and
                the difference between "this transfer" and "part of it" is the
                whole reason the figure above changed. */}
            {partial
              ? `${matchable.length + flagged} dari ${batchOrderCount} pesanan`
              : `${matchable.length + flagged} pesanan`}
            {single ? ` · ${single.method}` : null}
            {clients.size > 1 && ` · ${clients.size} client`}
          </div>
          {!single && (
            // Spelled out, because one reference across several transfers is
            // the unusual case and the operator should be choosing it: it is
            // right for one bank line covering several PIC deposits, and wrong
            // if these arrived separately.
            <ul className="mt-2 flex flex-col gap-1 border-t border-stroke-soft-200 pt-2 text-xs text-text-sub-600">
              {batches.map((b) => (
                <li key={b.key} className="flex justify-between gap-3">
                  <span className="truncate">
                    {b.depositedBy} · {b.depositedOn}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {formatRupiah(b.totalAmount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Singular only when there IS one deposit. With several selected the
            old copy claimed "satu baris mutasi rekening" two lines above a
            hint saying one reference may cover several deposits — the modal
            contradicted itself on the one question the operator is answering.
            Two PICs transferring separately are two statement lines. */}
        {partial && (
          <Alert status="warning" size="lg">
            Hanya <b>{matchable.length}</b> pesanan yang dipilih dari setoran
            ini. Sisanya tetap menunggu verifikasi dan perlu dicocokkan terpisah
            — pastikan memang begitu bunyinya di rekening koran.
          </Alert>
        )}

        <Alert status="information" size="lg">
          {batches.length > 1 ? (
            <>
              Semua setoran ini akan menyandang <b>referensi yang sama</b>.
              Lakukan hanya bila satu baris mutasi memang menutup semuanya —
              transfer terpisah punya barisnya sendiri.
            </>
          ) : (
            <>
              Cocokkan setoran ini ke <b>satu baris mutasi rekening</b>.
            </>
          )}{' '}
          Referensi mutasi adalah bukti pencocokan — bukan sekadar centang — dan
          tersimpan di jejak audit.
        </Alert>

        {flagged > 0 && (
          <Alert status="warning" size="lg">
            {flagged} pesanan sedang ditandai bermasalah dan akan dilewati.
            Selesaikan satu per satu setelah ini.
          </Alert>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cod-bank-ref">
            Referensi / ID mutasi bank{' '}
            <span className="text-error-base">*</span>
          </Label>
          {/* InputRoot is what draws the box — `Input` on its own is a bare
              <input> and renders as plain text next to the label, which is
              exactly how this shipped: the one required field on the dialog
              looked like a caption. Same note already sits in ConfirmCodModal. */}
          <InputRoot size="md">
            <Input
              id="cod-bank-ref"
              value={bankRef}
              onChange={(e) => setBankRef(e.target.value)}
              placeholder="mis. TRX20260916008841"
              maxLength={200}
            />
          </InputRoot>
          <Hint>
            Salin apa adanya dari rekening koran — formatnya bebas. Satu
            referensi boleh dipakai untuk beberapa setoran.
          </Hint>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cod-match-note">Catatan (opsional)</Label>
          <Textarea
            id="cod-match-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Konteks pencocokan bila perlu"
            rows={2}
          />
        </div>
      </div>
    </FormModal>
  );
};

/**
 * Confirmed by Finance — the terminal, irreversible step.
 *
 * A dialog rather than a direct action, and that asymmetry is deliberate: a
 * single confirm form IS its own confirmation, but this one cannot be undone and
 * there is no reopen path, so the numbers get restated before anyone commits.
 */
export const ConfirmFinanceModal: React.FC<{
  selection: CodFinanceSelection | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ selection, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);

  if (!selection || selection.orders.length === 0) return null;

  const batches = selection.batches;
  // `eligible` is matched AND unflagged — the same rule the endpoint
  // re-enforces. Selecting an unmatched order does not make it confirmable.
  const orders = selection.orders.filter((o) => o.eligible);
  const total = orders.reduce((sum, o) => sum + o.depositedAmount, 0);
  const batchOrderCount = batches.reduce((sum, b) => sum + b.totalOrders, 0);
  const partial = !selection.whole;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await confirmCodFinance({
        shipmentIds: orders.map((o) => o.id),
        // One shared audit id when the gesture spans several deposits, so the
        // trail can tell one decision from many made close together.
        bulk: batches.length > 1,
      });
      toast.success(`${res.affected.length} pesanan dikonfirmasi Finance`);
      onDone();
      onClose();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // One deposit or many — the prototype words the question differently for
  // each, because "setoran 06 Sep 2026?" is answerable at a glance and
  // "3 setoran?" is not.
  const single = batches.length === 1 ? batches[0] : null;
  // A cherry-picked set is named by its orders, not by its deposit: calling it
  // "setoran 22 Sep" when two of that deposit's five orders are staying behind
  // would have the dialog claim more than the button does.
  const title = partial
    ? `Confirmed by Finance — ${orders.length} pesanan?`
    : single
      ? `Confirmed by Finance — setoran ${single.depositedOn}?`
      : `Confirmed by Finance — ${batches.length} setoran?`;

  return (
    <FormModal
      open
      onClose={onClose}
      size="sm"
      icon={<i className="ri-check-line" />}
      iconTone="primary"
      title={title}
      footer={
        <div className="flex items-center justify-between gap-3">
          <ActorNote />
          <div className="flex gap-2">
            <Button style="stroke" onClick={onClose}>
              Batal
            </Button>
            <Button onClick={submit} disabled={saving || orders.length === 0}>
              {saving ? 'Memproses…' : 'Ya, konfirmasi'}
            </Button>
          </div>
        </div>
      }
    >
      {/* Prose, not a figure box. This dialog is the last thing between an
          operator and an irreversible write, so it restates the decision as a
          sentence they can check against what they meant to do — which deposit,
          how many orders, how much, and that it is final. A big number in a
          card reads as a receipt for something already done. */}
      <p className="text-paragraph-small text-text-sub-600">
        {single && !partial ? (
          <>
            Setoran <b className="text-text-strong-950">{single.depositedBy}</b>{' '}
            · {single.method} · {single.depositedOn}
            {single.financeBankRef ? (
              <> · mutasi {single.financeBankRef}</>
            ) : null}
            :{' '}
          </>
        ) : null}
        {/* Lower-case only when it CONTINUES the single-deposit prefix above
            ("Setoran andi@… · bank_transfer · 16 Sep: konfirmasi 2 pesanan…").
            With several deposits there is no prefix, so the sentence starts
            here and was rendering as "konfirmasi 2 pesanan senilai…". */}
        {single && !partial ? 'konfirmasi' : 'Konfirmasi'}{' '}
        <b className="text-text-strong-950">{orders.length} pesanan</b> senilai{' '}
        <b className="text-text-strong-950">{formatRupiah(total)}</b>
        {partial ? (
          <>
            {' '}
            —{' '}
            <b className="text-text-strong-950">
              {orders.length} dari {batchOrderCount}
            </b>{' '}
            pesanan di{' '}
            {single ? (
              <>
                setoran{' '}
                <b className="text-text-strong-950">{single.depositedBy}</b>
              </>
            ) : (
              <>
                <b className="text-text-strong-950">{batches.length} setoran</b>
              </>
            )}
          </>
        ) : single ? null : (
          <>
            {' '}
            dari{' '}
            <b className="text-text-strong-950">{batches.length} setoran</b>
          </>
        )}{' '}
        sebagai <b className="text-text-strong-950">Selesai</b>. Tahap final —
        tidak bisa dikoreksi lagi.
      </p>
      {partial && (
        <p className="mt-2 text-xs text-text-sub-600">
          Sisa pesanan di setoran ini tetap menunggu verifikasi.
        </p>
      )}
    </FormModal>
  );
};

/**
 * Flag a deposit that does not check out, and route it.
 *
 * Per order, because a problem is found in one line of a statement rather than
 * across a whole transfer. `return_to_pic` is the only action that moves the
 * ledger; the retired deposit keeps its amount and proof rather than being
 * deleted, so a repeat offender stays visible.
 */
export const FinanceIssueModal: React.FC<{
  order: CodFinanceOrder | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ order, onClose, onDone }) => {
  const [action, setAction] = useState<CodFinanceIssueAction>('hold');
  const [reason, setReason] = useState<CodFinanceIssueReason | ''>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Already flagged → the useful default is clearing it; otherwise holding.
    setAction(order?.financeIssueReason ? 'clear' : 'hold');
    setReason(order?.financeIssueReason ?? '');
    setNote(order?.financeIssueNote ?? '');
  }, [order]);

  if (!order) return null;

  const clearing = action === 'clear';

  const submit = async () => {
    if (!clearing && !reason) {
      toast.error('Jenis masalah wajib dipilih');
      return;
    }
    if (!clearing && !note.trim()) {
      toast.error(
        'Catatan wajib diisi — PIC perlu tahu apa yang harus dibetulkan'
      );
      return;
    }
    setSaving(true);
    try {
      await flagCodFinanceIssue(order.id, {
        action,
        reason: clearing ? undefined : (reason as CodFinanceIssueReason),
        note: clearing ? undefined : note.trim(),
      });
      toast.success(COD_FINANCE_ISSUE_ACTION_LABELS[action]);
      onDone();
      onClose();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title="Tandai bermasalah"
      subtitle={`${order.bookingId} · ${formatRupiah(order.depositedAmount)}`}
      footer={
        <div className="flex items-center justify-between gap-3">
          <ActorNote />
          <div className="flex gap-2">
            <Button style="stroke" onClick={onClose}>
              Batal
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Tindakan</Label>
          <Select
            value={action}
            onValueChange={(v) => setAction(v as CodFinanceIssueAction)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COD_FINANCE_ISSUE_ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>
                  {COD_FINANCE_ISSUE_ACTION_LABELS[a]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {action === 'return_to_pic' && (
            <Hint>
              Pesanan kembali ke <b>Menunggu Deposit PIC</b>. Setoran ini
              disimpan sebagai riwayat, PIC harus menyetor ulang.
            </Hint>
          )}
        </div>

        {!clearing && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label>
                Jenis masalah <span className="text-error-base">*</span>
              </Label>
              <Select
                value={reason}
                onValueChange={(v) => setReason(v as CodFinanceIssueReason)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih jenis masalah" />
                </SelectTrigger>
                <SelectContent>
                  {COD_FINANCE_ISSUE_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {COD_FINANCE_ISSUE_REASON_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                Catatan <span className="text-error-base">*</span>
              </Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Jelaskan temuan untuk PIC / auditor"
                rows={3}
              />
            </div>
          </>
        )}
      </div>
    </FormModal>
  );
};
